import React from 'react';
import './daily-fee.styles.scss';
import QuickActionCard, {
  AddCard,
} from '../../components/card/quick-action-card/quick-action-card.component';
import { useHistory } from 'react-router-dom';
import { CreateFeeType, CreateGroupFeeType } from './FeeTypeForm';
import { Edit, Printer } from 'react-feather';
import CreateCollection from './CreateCollection';
import PageLayout from '../../components/PageLayout';
import {
  DatePicker,
  DatePickerInput,
  Link,
  Loading,
  Select,
  SelectItem,
} from 'carbon-components-react';
import { useQueryParams } from '../../utils/useQueryParams';
import {
  formatCurrency,
  formatCurrencyV2,
  formatDate,
  getItemById,
  getQueryString,
  getStatusColor,
  hasExpired,
  isAllTrue,
} from '../../utils/common.util';
import useSWR from 'swr';
import PrintOutHead from '../../components/PrintOutHead';
import { PrintView } from '../../components/PrintView/print-view.component';
import CustomButton from '../../components/custom-button/custom-btn.component';
import useFetchData from '../../utils/useFetchData';
import { DailyFeeSettings } from './settings/DailyFeeSettings';
import ReportsModal from './reports/ReportsModal';
import MakeAdvancePayment from './MakeAdvancePayment';
import MakeArrearsPayment from './arrears-page/MakeArrearsPayment';
import { useClassAccessList } from '../../api/class.service';
import { useUrlState } from '../../utils/useUrlState';
import { useIsAllowed } from '../../utils/useCurrentUser';
import Dropdown from '../../components/dropdown/Dropdown';

export default function DailyFeePage() {
  const [isOpen, setIsOpen] = React.useState('');
  const history = useHistory();

  const {
    dailyBillingFeeId: urlDailyBillingFeeId,
    classId: urlClassId,
    paymentDate: urlPaymentDate,
    genericGroupingId: urlGenericGroupingId,
  } = useQueryParams(
    'dailyBillingFeeId',
    'classId',
    'paymentDate',
    'genericGroupingId',
  );

  const [query, setQuery] = useUrlState({
    dailyBillingFeeId: urlDailyBillingFeeId ?? '',
    classId: urlClassId ?? '',
    dateFrom: urlPaymentDate ?? '',
    academicYearId: '',
    termId: '',
    studentId: '',
    genericGroupingId: urlGenericGroupingId ?? '',
  });

  const { classAccessList: classes } = useClassAccessList();
  const { data } = useFetchData(`daily-billing/fees/assigned-to-user`);
  const feeTypes = data?.response ?? [];
  const selectedFeetype = getItemById(feeTypes, query.dailyBillingFeeId);
  const isGeneric = selectedFeetype?.type === 'Generic';

  const {
    data: paymentsData,
    isLoading: isLoadingPayments,
    error,
  } = useSWR(
    isAllTrue(
      isGeneric ? query.genericGroupingId : query.classId,
      query.dailyBillingFeeId,
      query.dateFrom,
    )
      ? `daily-billing/revenue/list?${getQueryString(query)}`
      : null,
  );

  const feeType = paymentsData?.payment?.dailyBillingFee ?? null;
  const revenues = paymentsData?.payment?.revenues ?? [];
  const totalAmount = paymentsData?.payment?.totalAmount ?? 0;
  const totalDepositAmountAppliedToday =
    paymentsData?.payment?.totalDepositAmountAppliedToday ?? 0;
  const totalArrearsApplied = paymentsData?.payment?.revenues?.reduce(
    (acc, entry) => acc + parseFloat(entry.arrearsApplied || 0),
    0,
  );

  function isDisabled(item) {
    return hasExpired(item?.invoice?.gracePeriodEndDate);
  }

  const isDraft = revenues?.some(
    (entry) => entry?.paymentStatus?.toUpperCase() === 'DRAFT',
  );

  const { isAllowed, isLoadingUser } = useIsAllowed();

  const add_advance = isAllowed(['DailyBillingFeesAdvancePaymentAccess']);
  const add_fee_type = isAllowed(['DailyBillingFeesTypeCreationAccess']);

  const actions = [
    { label: 'Class Based', onClick: () => setIsOpen('classFeeType') },
    {
      label: (
        <span title="Instead of class, its based on a group of your own definition e.g grouping students based on location, etc.">
          Group Based
        </span>
      ),
      onClick: () => setIsOpen('groupFeeType'),
    },
  ];

  const advance_actions = [
    ...(add_advance
      ? [{ label: 'Pay Deposit', onClick: () => setIsOpen('advance') }]
      : []),
    {
      label: 'Pay Arrears',
      onClick: () => setIsOpen('arrears'),
    },
  ];

  const advance_view_actions = [
    {
      label: 'Deposit Payments',
      onClick: () => history.push('/daily-fees/advance-payments'),
    },
    {
      label: 'Student Arrears',
      onClick: () => history.push('/daily-fees/student-arrears'),
    },
    {
      label: 'Arrears Payments',
      onClick: () => history.push('/daily-fees/arrears-payments'),
    },
  ];

  return (
    <PageLayout
      className="daily-fee-page"
      title={`Daily Fee Collection`}
      isLoading={isLoadingUser}
    >
      <div className="card-row-5">
        <AddCard title="COLLECT FEES" onClick={() => setIsOpen('collect')} />

        {isAllowed([
          'DailyBillingFeesTypeCreationAccess',
          'DailyBillingFeesTypeCreationAccessReadOnly',
        ]) && (
          <QuickActionCard
            description="Fee Types"
            leftActionText={
              add_fee_type && (
                <Dropdown actions={actions}>
                  <Link to="" className="link">
                    ADD
                  </Link>
                </Dropdown>
              )
            }
            rightActionText="VIEW"
            actionEvent2={() => history.push('/daily-fees/types')}
          >
            {/* <ContactGreen color="#fff" /> */}
          </QuickActionCard>
        )}

        {isAllowed([
          'DailyBillingFeesAdvancePaymentAccess',
          'DailyBillingFeesAdvancePaymentAccessReadOnly',
        ]) && (
          <QuickActionCard
            description="Deposit & Arrears"
            leftActionText={
              <Dropdown actions={advance_actions}>
                <Link to="" className="link">
                  ADD
                </Link>
              </Dropdown>
            }
            rightActionText={
              <Dropdown actions={advance_view_actions}>
                <Link to="" className="link">
                  VIEW
                </Link>
              </Dropdown>
            }
          />
        )}

        <QuickActionCard
          description="Bills & Reports"
          actionEvent1={() => setIsOpen('reports')}
          leftActionText="VIEW"
          hideAction2={true}
        >
          {/* <ContactGreen color="#fff" /> */}
        </QuickActionCard>

        {isAllowed([
          'DailyBillingFeesSettingsAccess',
          'DailyBillingFeesSettingsAccessReadOnly',
        ]) && (
          <QuickActionCard
            description="Settings"
            actionEvent1={() => setIsOpen('settings')}
            leftActionText="VIEW"
            hideAction2={true}
          >
            {/* <ContactGreen color="#fff" /> */}
          </QuickActionCard>
        )}
      </div>

      {/* FILTERS  */}
      <p className="mt-30 text-12">
        Select at least the fee type, class and date filters to show report.
      </p>
      <div className="mt-10 card-row-3">
        <Select
          labelText="Fee Type"
          value={query.dailyBillingFeeId}
          onChange={(e) =>
            setQuery({
              dailyBillingFeeId: e.target.value,
              genericGroupingId: '',
              classId: '',
            })
          }
        >
          <SelectItem selected value="" text="" />
          {feeTypes?.map((item) => (
            <SelectItem
              key={item.id}
              text={
                item.name +
                (isDisabled(item) && item?.invoice?.status
                  ? ` - [${item.invoice.status}]`
                  : '')
              }
              value={item.id}
              disabled={isDisabled(item)}
            />
          ))}
        </Select>
        <Select
          labelText={isGeneric ? 'Group' : 'Class'}
          disabled={!query.dailyBillingFeeId}
          onChange={(e) =>
            setQuery(
              isGeneric
                ? { genericGroupingId: e.target.value, classId: '' }
                : { classId: e.target.value, genericGroupingId: '' },
            )
          }
          value={isGeneric ? query.genericGroupingId : query.classId}
        >
          <SelectItem disabled value="" text="" />
          {selectedFeetype?.genericAmounts?.map((item) => (
            <SelectItem key={item.id} text={item.name} value={item.id} />
          ))}
          {selectedFeetype?.classAmounts?.map((item) => (
            <SelectItem
              key={item.id}
              text={item.classDescription}
              value={item.classId}
            />
          ))}
        </Select>
        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="start-date-picker"
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                dateFrom: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ dateFrom: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="start-date"
            id="start-date"
            name="dateFrom"
            labelText="Date"
            value={query.dateFrom}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ dateFrom: e.target.value })}
          />
        </DatePicker>
      </div>

      {isLoadingPayments && (
        <Loading small={true} withOverlay={false} className="mt-20" />
      )}
      {!isLoadingPayments && (
        <div className="mt-20">
          {error && (
            <p className="text-14 mt-10 text-danger">{error.message}</p>
          )}
          {revenues?.length > 0 && (
            <>
              <PrintOutHead
                title={`Daily Billing Fee Report`}
                headings={[
                  {
                    label: 'Fee Type',
                    value: feeType?.name ?? 'N/A',
                  },
                  {
                    label: 'Class Amount',
                    value: formatCurrencyV2(feeType?.amount) ?? 'N/A',
                  },
                  {
                    label: 'Class',
                    value: getItemById(classes, query.classId)?.description,
                  },
                  {
                    label: 'Generic Grouping',
                    value: getItemById(
                      selectedFeetype?.genericAmounts,
                      query.genericGroupingId,
                    )?.description,
                  },
                  {
                    label: 'Payment Date',
                    value: formatDate(query?.dateFrom) ?? 'N/A',
                  },
                ]}
              />
              <PrintView>
                <div
                  className="mt-40 mb-10 d-flex"
                  style={{
                    gap: '2opx',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                  }}
                >
                  <div className="d-flex" style={{ gap: '20px' }}>
                    <span>Total Students: {revenues.length ?? 0}</span>
                    <span>
                      Students Present:{' '}
                      {paymentsData?.payment?.totalStudentsPresent ?? 0}
                    </span>
                    <span>
                      Students Absent:{' '}
                      {paymentsData?.payment?.totalStudentsAbsent ?? 0}
                    </span>
                  </div>
                </div>
                <div style={{ overflow: 'auto' }}>
                  <table className="bx--data-table table-white">
                    <thead>
                      <tr>
                        {/* <th>Student ID</th> */}
                        <th>Unique Id</th>
                        <th>Name</th>
                        <th>Class</th>
                        <th>Status</th>
                        {!isDraft && (
                          <th>
                            Arrears Paid
                            <br /> (GHS)
                          </th>
                        )}
                        <th>
                          Deposit Allocated <br /> (GHS)
                        </th>
                        <th>
                          Amount Paid <br /> (GHS)
                        </th>
                        <th>Payment Mode</th>
                        <th>Payment Date</th>
                        <th>Payment Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenues?.map((entry, idx) => (
                        <tr key={entry.id}>
                          <td>{entry.student.studentId}</td>
                          <td>
                            {entry.student.firstName} {entry.student.lastName}
                          </td>
                          <td>{entry.student.classDescription}</td>
                          <td
                            className={`font-bold ${getStatusColor(entry?.attendanceStatus)}`}
                          >
                            {entry.attendanceStatus}
                          </td>
                          {!isDraft && <td>{entry.arrearsApplied}</td>}
                          <td>{entry.depositPaymentAmount}</td>
                          <td>{entry.amountPaid}</td>
                          <td>{entry.paymentMode}</td>
                          <td>{formatDate(entry.paymentDate)}</td>
                          <td>{entry.paymentStatus}</td>
                        </tr>
                      ))}
                      <tr className="font-bold">
                        <td>Totals</td>
                        <td colSpan="3"></td>
                        {!isDraft && (
                          <td>{formatCurrency(totalArrearsApplied)}</td>
                        )}
                        <td>
                          {formatCurrency(totalDepositAmountAppliedToday)}
                        </td>
                        <td>{formatCurrency(totalAmount)}</td>
                        <td colSpan="4"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </PrintView>
            </>
          )}
          {revenues?.length === 0 && (
            <p className="text-14 mt-30 text-grey">
              No daily fees collected for the selected filters.
            </p>
          )}

          {revenues?.length > 0 && (
            <div
              className="mt-30"
              style={{
                display: 'flex',
                justifySelf: 'end',
                gap: '20px',
                alignItems: 'center',
              }}
            >
              {isDraft && (
                <Link href={`/daily-fees/edit?${getQueryString(query)}`}>
                  <CustomButton classes="primary">
                    <Edit size="16" strokeWidth="2px" /> &nbsp; Edit
                  </CustomButton>
                </Link>
              )}
              <CustomButton classes="secondary" onClick={() => window.print()}>
                <Printer size="16" strokeWidth="2px" /> &nbsp; Print
              </CustomButton>
            </div>
          )}
        </div>
      )}

      <CreateFeeType
        isOpen={isOpen === 'classFeeType'}
        closeModal={() => setIsOpen('')}
        refetch={() => {}}
      />

      <CreateGroupFeeType
        isOpen={isOpen === 'groupFeeType'}
        closeModal={() => setIsOpen('')}
      />

      <CreateCollection
        isOpen={isOpen === 'collect'}
        closeModal={() => setIsOpen('')}
      />

      <ReportsModal
        isOpen={isOpen === 'reports'}
        closeModal={() => setIsOpen('')}
      />

      <DailyFeeSettings
        isOpen={isOpen === 'settings'}
        closeModal={() => setIsOpen('')}
      />

      <MakeAdvancePayment
        isOpen={isOpen === 'advance'}
        closeModal={() => setIsOpen('')}
        onSuccess={(data) => {
          history.push(
            `/daily-fees/advance-payments?${getQueryString({
              dailyBillingFeeId: data?.dailyBillingFee?.id,
              studentId: data?.student?.id,
            })}`,
          );
        }}
      />

      <MakeArrearsPayment
        isOpen={isOpen === 'arrears'}
        closeModal={() => setIsOpen('')}
        onSuccess={(data) => {
          history.push(
            `/daily-fees/arrears-payments?${getQueryString({
              dailyBillingFeeId: data?.dailyBillingFee?.id,
              studentId: data?.student?.id,
            })}`,
          );
        }}
      />
    </PageLayout>
  );
}
